import classNames from 'classnames'
import { MdInfoOutline } from 'react-icons/md'

import type { ColDef, ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community'
import type { ReactElement } from 'react'
import type { Menu } from '~/components/ActionMenu'
import type { ScoreComponentProps } from '~/components/Score'
import type { IASOAdvancedKeyword } from '~/types/aso'

import ActionMenu from '~/components/ActionMenu'
import AppLogo from '~/components/AppLogo'
import Badge from '~/components/Badge'
import ScoreCellRenderer from '~/components/CellRenderers/ScoreCellRenderer'
import { FormatTextCellRenderer } from '~/components/CellRenderers/TextCellRenderer'
import sharedHeaderTemplate from '~/components/HeaderTemplate'
import Icon from '~/components/Icon'
import IconRanking from '~/components/IconRanking'
import MixIcon from '~/components/MixIcon'
import Rating from '~/components/Rating'
import Tooltip from '~/components/Tooltip'
import { MAX_RATING_VALUE } from '~/const'
import { actionsColDef, scoreColDef } from '~/utils/table'

const ActionsCellRenderer = ({ context, data }: ICellRendererParams<IASOAdvancedKeyword>): ReactElement => {
  const { handleMarkAsBrand } = context

  const menu: Menu = {
    actions: [
      {
        action: () => handleMarkAsBrand(data?.keyword, true),
        label: 'Mark as Brand',
      },
      {
        action: () => handleMarkAsBrand(data?.keyword, false),
        label: 'Remove Brand tag',
      },
    ],
  }

  return <ActionMenu menu={menu} menuClassName="ASO-keyword-action-menu" placement="bottom" />
}

const KeywordCellRenderer = ({ data }: ICellRendererParams<IASOAdvancedKeyword>): ReactElement => {
  if (data) {
    const { is_asa_keyword, is_branded_keyword, keyword, keyword_translation } = data

    return (
      <div className="keyword-cell-renderer">
        <div className="keyword-cell-renderer__text">
          <div className="keyword">{keyword}</div>
          <div className="keyword-translation">{keyword_translation} (en)</div>
        </div>
        <div className="keyword-cell-renderer__badges">
          {is_asa_keyword && <Badge className="asa-badge" value="ASA" />}
          {is_branded_keyword && <Badge className="branded-badge" value="Brand" />}
        </div>
      </div>
    )
  }
  return <div />
}

const KeywordPresenceCellRenderer = ({ context, data }: ICellRendererParams<IASOAdvancedKeyword>): ReactElement => {
  if (data) {
    return (
      <div className="keyword-presence-cell-renderer">
        <Icon color={data.keyword_placement_mwm === 3 ? '#ff5301' : '#aeaeae'} name="title" size={21} />
        <Icon color={data.keyword_placement_mwm === 2 ? '#ff5301' : '#aeaeae'} name="subtitle" size={21} />
        <Icon
          color={context.metadata?.keywords?.includes(data.keyword) ? '#ff5301' : '#aeaeae'}
          name="keyword"
          size={21}
        />
        <MixIcon isActive={data.keyword_placement_mwm === 1} />
      </div>
    )
  }
  return <div />
}

const QualityCellRenderer = ({
  value,
}: ICellRendererParams<IASOAdvancedKeyword, IASOAdvancedKeyword['keyword_quality']>): ReactElement => {
  return <Rating maxValue={MAX_RATING_VALUE} value={value || 0} />
}

const AllClusterRankCellRenderer = ({
  value: allClusterRanks,
}: ICellRendererParams<IASOAdvancedKeyword, IASOAdvancedKeyword['all_cluster_ranks']>): ReactElement => {
  const limit = 10
  return (
    <div className="all-cluster-rankings-cell-renderer">
      {allClusterRanks
        ?.slice(0, limit)
        .map(({ icon_url, rank }, idx) => <IconRanking appLogoUrl={icon_url} key={idx} rank={rank} />)}
      {allClusterRanks && allClusterRanks.length > limit ? (
        <Tooltip
          align={{ points: ['br', 'tr'] }}
          overlay={
            <div className="all-cluster-rankings-cell-renderer__tooltip-content">
              {allClusterRanks.slice(limit).map(({ icon_url, rank }, idx) => (
                <IconRanking appLogoUrl={icon_url} key={idx} rank={rank} />
              ))}
            </div>
          }
          placement="top"
          trigger="hover"
        >
          <div className="keyword-ranking-badge">+{allClusterRanks.length - limit}</div>
        </Tooltip>
      ) : null}
    </div>
  )
}

const RankCellRenderer = ({
  value: ranks,
}: ICellRendererParams<IASOAdvancedKeyword, IASOAdvancedKeyword['ranks']>): ReactElement => {
  return (
    <div className="keyword-ranking-cell-renderer">
      {ranks && ranks.length > 2 ? (
        <>
          <IconRanking appLogoUrl={ranks[0].icon_url} rank={ranks[0].rank} />
          <Tooltip
            align={{ points: ['bl', 'tl'] }}
            mouseEnterDelay={0.2}
            overlay={
              <div className="rank-cell-renderer__tooltip-content">
                {ranks.slice(1, ranks.length - 1).map(({ icon_url, rank }, idx) => (
                  <IconRanking appLogoUrl={icon_url} key={idx} rank={rank} />
                ))}
              </div>
            }
            placement="topRight"
            trigger={['hover']}
          >
            <div className="keyword-ranking-badge">{`+ ${ranks.length - 2}`}</div>
          </Tooltip>
          <IconRanking appLogoUrl={ranks[ranks.length - 1].icon_url} rank={ranks[ranks.length - 1].rank} />
        </>
      ) : (
        ranks?.map(({ icon_url, rank }, idx) => <IconRanking appLogoUrl={icon_url} key={idx} rank={rank} />)
      )}
    </div>
  )
}

const FirstRankInstallCellRenderer = ({
  data,
  value,
}: ICellRendererParams<IASOAdvancedKeyword, IASOAdvancedKeyword['max_est_installs']>): ReactElement => {
  if (data) {
    const { all_cluster_ranks } = data

    return (
      <div className="first-rank-install-cell-renderer">
        <AppLogo appLogoUrl={all_cluster_ranks[0].icon_url} size="small" />
        <span className="value">{(value || 0).toFixed(0)}</span>
      </div>
    )
  }
  return <div />
}

const ASACellRenderer = ({ data, value }: ICellRendererParams<IASOAdvancedKeyword>): ReactElement => {
  if (data) {
    return (
      <div className="asa-cell-renderer">
        {data.asa_reliability && (
          <Tooltip
            align={{ points: ['bl', 'tl'] }}
            mouseEnterDelay={0.2}
            overlay="Not significant because of low volume"
            placement="topRight"
            trigger={['hover']}
          >
            <div>
              <MdInfoOutline className="warning-wrapper" color="#fedf8f" size={16} />
            </div>
          </Tooltip>
        )}
        {(value * 100).toFixed(2)}%
      </div>
    )
  }

  return <div />
}

const LatestVersionCellRenderer = ({
  data,
  value,
}: ICellRendererParams<IASOAdvancedKeyword, IASOAdvancedKeyword['latest_app_version']>): ReactElement => {
  if (data && value) {
    const classes = classNames('latest-version-badge', { 'is-latest': data.is_last_app_version })
    return <Badge className={classes} value={value} />
  }
  return <div />
}

const columns: ColDef[] = [
  {
    cellRenderer: KeywordCellRenderer,
    field: 'keyword',
    filter: 'agTextColumnFilter',
    headerComponent: 'customHeader',
    headerName: 'Keyword',
    headerTooltip: 'Keyword',
    menuTabs: ['filterMenuTab'],
    minWidth: 250,
    suppressMovable: true,
    suppressSizeToFit: true,
  },
  {
    cellRenderer: KeywordPresenceCellRenderer,
    field: 'present_in',
    headerComponent: 'customHeader',
    headerName: 'Present in',
    headerTooltip: 'Present in',
    minWidth: 170,
    resizable: false,
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    suppressSizeToFit: true,
    width: 140,
  },
  {
    cellRenderer: QualityCellRenderer,
    comparator: (valueA: number, valueB: number): number => {
      return valueA > valueB ? 1 : -1
    },
    field: 'keyword_quality',
    filter: 'agSetColumnFilter',
    headerComponent: 'customHeader',
    headerName: 'Quality',
    headerTooltip: 'Quality',
    menuTabs: ['filterMenuTab'],
    minWidth: 170,
    resizable: false,
    suppressMovable: true,
    suppressSizeToFit: true,
    width: 170,
  },
  {
    cellRenderer: FormatTextCellRenderer,
    field: 'avg_sov',
    headerComponent: 'customHeader',
    headerName: 'ASA - Share of Voice',
    headerTooltip: 'ASA - Share of Voice',
    hide: true,
    minWidth: 170,
    resizable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    suppressSizeToFit: true,
    valueFormatter: ({ value }: ValueFormatterParams<IASOAdvancedKeyword, IASOAdvancedKeyword['avg_sov']>) =>
      value
        ? value
            .split('-')
            .map((v: string) => `${v}%`)
            .join('-')
        : '',
    width: 220,
  },
  {
    cellRenderer: ASACellRenderer,
    field: 'avg_ttr',
    headerComponent: 'customHeader',
    headerName: 'ASA - Tap Through Rate',
    headerTooltip: 'ASA - Tap Through Rate',
    hide: true,
    minWidth: 170,
    resizable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    suppressSizeToFit: true,
    width: 220,
  },
  {
    cellRenderer: ASACellRenderer,
    field: 'avg_cr',
    headerComponent: 'customHeader',
    headerName: 'ASA - Conversion Rate',
    headerTooltip: 'ASA - Conversion Rate',
    hide: true,
    minWidth: 170,
    resizable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    suppressSizeToFit: true,
    width: 220,
  },
  {
    cellRenderer: ASACellRenderer,
    field: 'avg_install_rate',
    headerComponent: 'customHeader',
    headerName: 'ASA - Install Rate',
    headerTooltip: 'ASA - Install Rate',
    hide: true,
    minWidth: 170,
    resizable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    suppressSizeToFit: true,
    width: 220,
  },
  scoreColDef({
    cellRenderer: ScoreCellRenderer,
    field: 'popularity',
    headerComponent: 'customHeader',
    headerName: 'Popularity',
    headerTooltip: 'Popularity',
    valueGetter: ({ data }: ValueGetterParams<IASOAdvancedKeyword>): ScoreComponentProps => ({
      type: 'popularity',
      value: data?.popularity || 0,
    }),
  }),
  {
    cellRenderer: RankCellRenderer,
    field: 'ranks',
    headerName: 'Ranks',
    headerTooltip: 'Ranks',
    resizable: false,
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    suppressSizeToFit: true,
    width: 200,
  },
  {
    cellRenderer: LatestVersionCellRenderer,
    field: 'latest_app_version',
    filter: 'agTextColumnFilter',
    headerComponentParams: {
      menuIcon: 'fa-bars',
      template: sharedHeaderTemplate({
        color: '#626262',
        headerSubtitle: 'containing this keyword',
        headerTitle: 'Last version',
      }),
    },
    headerName: '',
    headerTooltip: 'Last version containing this keyword',
    menuTabs: ['filterMenuTab'],
    resizable: false,
    suppressMovable: true,
    suppressSizeToFit: true,
    width: 200,
  },
  {
    cellRenderer: FirstRankInstallCellRenderer,
    field: 'max_est_installs',
    headerName: '#1 Installs',
    headerTooltip: '#1 Installs',
    resizable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    suppressSizeToFit: true,
    width: 160,
  },
  {
    cellRenderer: FormatTextCellRenderer,
    field: 'cluster_est_installs',
    headerName: 'Cluster installs',
    headerTooltip: 'Cluster installs',
    resizable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    suppressSizeToFit: true,
    valueFormatter: ({
      value,
    }: ValueFormatterParams<IASOAdvancedKeyword, IASOAdvancedKeyword['cluster_est_installs']>) =>
      Math.floor(value || 0).toString(),
    width: 160,
  },
  {
    cellRenderer: FormatTextCellRenderer,
    field: 'increment_installs',
    headerComponent: 'customHeader',
    headerName: 'Next incremental installs',
    headerTooltip: 'Next incremental installs',
    resizable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    suppressSizeToFit: true,
    valueFormatter: ({ value }) => Math.floor(value).toString(),
    width: 230,
  },
  scoreColDef({
    cellRenderer: ScoreCellRenderer,
    field: 'competitiveness_score',
    headerComponent: 'customHeader',
    headerName: 'Competitiveness',
    headerTooltip: 'Competitiveness',
    valueGetter: ({ data }: ValueGetterParams<IASOAdvancedKeyword>): ScoreComponentProps => ({
      isPercentage: false,
      total: 5,
      type: 'competitiveness',
      value: data?.competitiveness_score || 0,
    }),
  }),
  {
    cellRenderer: AllClusterRankCellRenderer,
    field: 'all_cluster_ranks',
    headerName: 'All cluster ranks',
    headerTooltip: 'All cluster ranks',
    minWidth: 560,
    resizable: false,
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    suppressSizeToFit: true,
  },
  actionsColDef(ActionsCellRenderer, {
    field: 'actions',
  }),
]

export default columns
